import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { InvitePreview, PlatformOrganization } from '@bingo/shared';
import { ShareLink } from '../../components/ShareLink.js';
import { ApiError } from '../../lib/api.js';
import { api } from '../../lib/api.js';
import { sendCommand } from '../../lib/commands.js';
import { useSession } from '../../lib/useSession.js';

/**
 * /admin/plataforma — só para quem administra a plataforma.
 *
 * Criar uma organização **já cria o convite do dono**: organização sem dono
 * é organização que ninguém consegue administrar, e consertar isso exigiria
 * ir ao banco.
 */

/** Sugere um identificador a partir do nome, sem acento nem espaço. */
function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60);
}

export function Plataforma() {
  const session = useSession();

  const [orgs, setOrgs] = useState<PlatformOrganization[] | null>(null);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');

  const reload = useCallback(() => {
    void api<PlatformOrganization[]>('/api/platform/organizations')
      .then(setOrgs)
      .catch(() => setOrgs([]));
  }, []);

  useEffect(reload, [reload]);

  if (!session.user?.isPlatformAdmin) {
    return (
      <main className="shell">
        <div className="empty-card">
          <p>Esta área é de quem administra a plataforma.</p>
          <Link className="btn" to="/inicio">
            Meus jogos
          </Link>
        </div>
      </main>
    );
  }

  const criar = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const created = await sendCommand<{
        organization: PlatformOrganization;
        invite: InvitePreview;
      }>('/api/platform/organizations', {
        body: {
          name: name.trim(),
          slug: (slugTouched ? slug : slugify(name)).trim(),
          ownerEmail: ownerEmail.trim(),
          ownerName: ownerName.trim() || null,
        },
      });

      setPreview(created.invite);
      setName('');
      setSlug('');
      setSlugTouched(false);
      setOwnerEmail('');
      setOwnerName('');
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível criar a organização.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="shell">
      <header className="shell-head">
        <div>
          <h1>Plataforma</h1>
          <p className="hint-text">Organizações do sistema.</p>
        </div>
        <Link className="btn" to="/admin">
          Administração
        </Link>
      </header>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}

      {preview ? (
        <ShareLink
          preview={preview}
          title="Convite do dono criado"
          onClose={() => setPreview(null)}
        />
      ) : null}

      <section>
        <h2>Nova organização</h2>

        <form className="form" onSubmit={(event) => void criar(event)}>
          <label className="field">
            <span>Nome</span>
            <input
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (!slugTouched) setSlug(slugify(event.target.value));
              }}
              placeholder="Paróquia São José"
              required
            />
          </label>

          <label className="field">
            <span>Identificador</span>
            <input
              value={slugTouched ? slug : slugify(name)}
              onChange={(event) => {
                setSlugTouched(true);
                setSlug(event.target.value);
              }}
              placeholder="paroquia-sao-jose"
              pattern="[a-z0-9-]+"
              required
            />
          </label>

          <label className="field">
            <span>E-mail do dono</span>
            <input
              type="email"
              value={ownerEmail}
              onChange={(event) => setOwnerEmail(event.target.value)}
              placeholder="dono@exemplo.com"
              required
            />
          </label>

          <label className="field">
            <span>Nome do dono (opcional)</span>
            <input value={ownerName} onChange={(event) => setOwnerName(event.target.value)} />
          </label>

          <button type="submit" className="btn primary block" disabled={busy}>
            {busy ? 'Criando…' : 'Criar e gerar convite do dono'}
          </button>
        </form>
      </section>

      <section>
        <h2>Organizações</h2>

        {orgs === null ? (
          <p className="hint-text">Carregando…</p>
        ) : orgs.length === 0 ? (
          <p className="hint-text">Nenhuma organização ainda.</p>
        ) : (
          <ul className="people-list">
            {orgs.map((org) => (
              <li key={org.id} className="person-row">
                <div>
                  <strong>{org.name}</strong>
                  <small>{org.slug}</small>
                </div>
                <div className="history-meta">
                  <em className="pill">{org.plan}</em>
                  <span>
                    {org.memberCount} {org.memberCount === 1 ? 'membro' : 'membros'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
